/**
 * EMAIL SENDS DETAIL VIEW - PROGRESSIVE TEST
 */

import React, { useState, useEffect } from 'react'
import { ArrowLeft, Mail, Search, RefreshCw, X, Download, Filter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'
import { useToast } from '@/hooks/use-toast'

interface EmailSend {
  id: string
  created_at: string
  from_email: string
  to_email: string
  subject: string
  campaign_name: string
  campaign_id: string | null
  event_type: string
  client_id: string | null
  stats_thread_id: string | null
  body_text: string | null
  // Additional fields from obsync_dynamic_reports
  message_id: string | null
  stats_id: string | null
  sl_email_lead_id: string | null
  reply_body: string | null
  sent_message_body: string | null
  sent_message_html: string | null
  sent_message_text: string | null
  data: any | null  // JSON field with additional metadata
  processed_for_activities: boolean | null
  updated_at: string | null
}

interface EmailSendsDetailViewProps {
  onBack: () => void
}

export const EmailSendsDetailView = ({ onBack }: EmailSendsDetailViewProps) => {
  console.log('📧 EmailSendsDetailView - Component starting to render')
  
  const [emailSends, setEmailSends] = useState<EmailSend[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [dateFilter, setDateFilter] = useState('all')
  const [campaignFilter, setCampaignFilter] = useState('all')
  const [campaigns, setCampaigns] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(100)
  const [selectedEmail, setSelectedEmail] = useState<EmailSend | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const { toast } = useToast()

  console.log('📧 State initialized, setting up effects')

  // Fetch campaigns list on mount
  useEffect(() => {
    console.log('📧 useEffect for campaigns triggered')
    fetchCampaigns()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch email sends when page or filters change
  useEffect(() => {
    console.log('📧 useEffect for email sends triggered', { currentPage, searchQuery, dateFilter, campaignFilter })
    fetchEmailSends()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery, dateFilter, campaignFilter])

  const fetchCampaigns = async () => {
    try {
      console.log('📧 Fetching campaigns...')
      const { data, error } = await supabase
        .from('obsync_dynamic_reports')
        .select('campaign_name')
        .eq('event_type', 'EMAIL_SENT')
        .not('campaign_name', 'is', null)
        .limit(1000)

      if (error) {
        console.error('❌ Campaign fetch error:', error)
        throw error
      }

      const uniqueCampaigns = Array.from(new Set(data.map(d => d.campaign_name).filter(Boolean)))
      console.log(`✅ Fetched ${uniqueCampaigns.length} unique campaigns`)
      setCampaigns(uniqueCampaigns)
    } catch (error) {
      console.error('❌ Error fetching campaigns:', error)
    }
  }

  const fetchEmailSends = async () => {
    try {
      setLoading(true)
      console.log(`📧 Fetching page ${currentPage} with filters...`)

      let query = supabase
        .from('obsync_dynamic_reports')
        .select('*', { count: 'exact' })
        .eq('event_type', 'EMAIL_SENT')

      // Apply date filter
      if (dateFilter !== 'all') {
        const now = new Date()
        let startDate: Date

        switch (dateFilter) {
          case 'today':
            startDate = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
            break
          case 'yesterday':
            startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000)
            startDate = new Date(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate())
            break
          case '7days':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
            break
          case '30days':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
            break
          case 'mtd':
            startDate = new Date(now.getUTCFullYear(), now.getUTCMonth(), 1)
            break
          default:
            startDate = new Date(0)
        }

        query = query.gte('created_at', startDate.toISOString())
      }

      // Apply campaign filter
      if (campaignFilter !== 'all') {
        query = query.eq('campaign_name', campaignFilter)
      }

      // Apply search filter
      if (searchQuery.trim()) {
        const search = searchQuery.toLowerCase()
        query = query.or(`to_email.ilike.%${search}%,from_email.ilike.%${search}%,subject.ilike.%${search}%,campaign_name.ilike.%${search}%`)
      }

      // Apply pagination
      const from = (currentPage - 1) * pageSize
      const to = from + pageSize - 1

      const { data, count, error } = await query
        .order('created_at', { ascending: false })
        .range(from, to)

      if (error) {
        console.error('❌ Query error:', error)
        throw error
      }

      console.log(`✅ Fetched ${data.length} sends (${from + 1}-${Math.min(to + 1, count || 0)} of ${count || 0} total)`)
      setEmailSends(data)
      setTotalCount(count || 0)

    } catch (error) {
      console.error('❌ Error fetching email sends:', error)
      toast({
        title: 'Error',
        description: 'Failed to fetch email sends',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const handleDateFilterChange = (value: string) => {
    setDateFilter(value)
    setCurrentPage(1)
  }

  const handleCampaignFilterChange = (value: string) => {
    setCampaignFilter(value)
    setCurrentPage(1)
  }

  const totalPages = Math.ceil(totalCount / pageSize)
  const startRecord = (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalCount)

  const handleRowClick = (email: EmailSend) => {
    console.log('📧 Row clicked:', email.id)
    setSelectedEmail(email)
    setIsDetailModalOpen(true)
  }

  console.log('📧 About to render JSX')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              console.log('⬅️ Back button clicked')
              onBack()
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Live Feed
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Email Send History</h1>
            <p className="text-muted-foreground mt-1">
              Complete history of all email sends from your campaigns
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Sends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalCount.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Showing {startRecord}-{endRecord}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Current Page
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{currentPage.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              of {totalPages.toLocaleString()} pages
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Page Size
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{pageSize}</div>
            <p className="text-xs text-muted-foreground mt-1">
              records per page
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{emailSends.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              on this page
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search all sends..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Date Filter */}
            <Select value={dateFilter} onValueChange={handleDateFilterChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select date range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="mtd">Month to Date</SelectItem>
              </SelectContent>
            </Select>

            {/* Campaign Filter */}
            <Select value={campaignFilter} onValueChange={handleCampaignFilterChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select campaign" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Campaigns</SelectItem>
                {campaigns.map(campaign => (
                  <SelectItem key={campaign} value={campaign}>
                    {campaign}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Email Send History</CardTitle>
              <CardDescription>
                Showing {startRecord.toLocaleString()}-{endRecord.toLocaleString()} of {totalCount.toLocaleString()} total sends
              </CardDescription>
            </div>
            
            {/* Pagination Controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1 || loading}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || loading}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || loading}
              >
                Next
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || loading}
              >
                Last
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Loading page {currentPage}...</p>
            </div>
          ) : emailSends.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No email sends found</p>
              <p className="text-sm text-muted-foreground mt-2">
                Try adjusting your filters or search query
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Thread ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailSends.map((send) => (
                      <TableRow 
                        key={send.id}
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleRowClick(send)}
                      >
                        <TableCell className="whitespace-nowrap">
                          {format(new Date(send.created_at), 'MMM d, yyyy HH:mm')}
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[200px] truncate" title={send.from_email}>
                            {send.from_email}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[200px] truncate" title={send.to_email}>
                            {send.to_email}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[300px] truncate" title={send.subject}>
                            {send.subject || <span className="text-muted-foreground italic">No subject</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{send.campaign_name}</Badge>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs text-muted-foreground">
                            {send.stats_thread_id?.slice(0, 8) || 'N/A'}
                          </code>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Bottom Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Showing {emailSends.length} records on this page
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Email Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-5xl h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4">
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Details
              </div>
              {selectedEmail && (
                <Badge variant="outline" className="font-normal">
                  {format(new Date(selectedEmail.created_at), 'MMM d, yyyy HH:mm')}
                </Badge>
              )}
            </DialogTitle>
            {selectedEmail && (
              <DialogDescription className="text-base pt-2 space-y-1">
                <div className="flex items-start gap-2">
                  <span className="font-medium text-foreground min-w-12">From:</span>
                  <span className="break-all">{selectedEmail.from_email}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-medium text-foreground min-w-12">To:</span>
                  <span className="break-all">{selectedEmail.to_email}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-medium text-foreground min-w-12">Re:</span>
                  <span className="break-all">{selectedEmail.subject || <span className="italic text-muted-foreground">No subject</span>}</span>
                </div>
              </DialogDescription>
            )}
          </DialogHeader>

          {selectedEmail && (
            <Tabs defaultValue="content" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="content">Email Content</TabsTrigger>
                <TabsTrigger value="details">Details & Metadata</TabsTrigger>
                <TabsTrigger value="technical">Technical Info</TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto pr-2 pt-4">
                {/* CONTENT TAB */}
                <TabsContent value="content" className="space-y-4 mt-0">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="outline" className="text-xs">
                        {selectedEmail.campaign_name}
                      </Badge>
                      {selectedEmail.processed_for_activities !== null && (
                        <Badge variant={selectedEmail.processed_for_activities ? "default" : "secondary"} className="text-xs">
                          {selectedEmail.processed_for_activities ? "Processed" : "Pending"}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Email Content - Prioritized */}
                  {selectedEmail.sent_message_html ? (
                    <div>
                      <h4 className="text-sm font-semibold mb-2">Email Content (HTML)</h4>
                      <div className="border rounded-lg p-6 bg-white dark:bg-slate-950 max-h-[450px] overflow-y-auto">
                        <div 
                          className="prose prose-sm max-w-none dark:prose-invert"
                          dangerouslySetInnerHTML={{ __html: selectedEmail.sent_message_html }}
                        />
                      </div>
                    </div>
                  ) : (selectedEmail.body_text || selectedEmail.sent_message_text || selectedEmail.sent_message_body) ? (
                    <div>
                      <h4 className="text-sm font-semibold mb-2">Email Content</h4>
                      <div className="border rounded-lg p-4 bg-muted/30 max-h-[450px] overflow-y-auto">
                        <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed">
                          {selectedEmail.body_text || selectedEmail.sent_message_text || selectedEmail.sent_message_body}
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <div className="border rounded-lg p-8 bg-muted/20 text-center">
                      <Mail className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mb-2">No email content available</p>
                      <p className="text-xs text-muted-foreground">
                        This email record doesn't contain body content in the database.
                      </p>
                      {selectedEmail.subject && (
                        <div className="mt-4 text-left">
                          <p className="text-xs font-semibold text-muted-foreground mb-1">Subject:</p>
                          <p className="text-sm">{selectedEmail.subject}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedEmail.reply_body && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold mb-2">Reply Content</h4>
                      <div className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-50/50 dark:bg-blue-950/30 rounded">
                        <pre className="text-sm whitespace-pre-wrap font-sans">
                          {selectedEmail.reply_body}
                        </pre>
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* DETAILS TAB */}
                <TabsContent value="details" className="space-y-4 mt-0">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Campaign</h4>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{selectedEmail.campaign_name}</p>
                        {selectedEmail.campaign_id && (
                          <p className="text-xs text-muted-foreground font-mono">{selectedEmail.campaign_id}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Client</h4>
                      {selectedEmail.client_id ? (
                        <p className="text-sm font-mono">{selectedEmail.client_id}</p>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">No client ID</p>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Additional Metadata - Parsed */}
                  {selectedEmail.data && typeof selectedEmail.data === 'object' && Object.keys(selectedEmail.data).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-3">Additional Metadata</h4>
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(selectedEmail.data).map(([key, value]) => {
                          if (value === null || value === undefined || value === '') return null;
                          
                          const formattedKey = key
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, l => l.toUpperCase());
                          
                          return (
                            <div key={key} className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">{formattedKey}</p>
                              <div className="text-sm">
                                {typeof value === 'object' ? (
                                  <details className="cursor-pointer">
                                    <summary className="text-xs text-blue-600 hover:text-blue-700">
                                      View object
                                    </summary>
                                    <pre className="text-xs bg-muted/50 p-2 rounded mt-1 overflow-x-auto">
                                      {JSON.stringify(value, null, 2)}
                                    </pre>
                                  </details>
                                ) : typeof value === 'boolean' ? (
                                  <Badge variant={value ? "default" : "secondary"} className="text-xs">
                                    {value ? 'Yes' : 'No'}
                                  </Badge>
                                ) : (
                                  <p className="break-all font-medium">{String(value)}</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {selectedEmail.updated_at && selectedEmail.updated_at !== selectedEmail.created_at && (
                    <div className="pt-4">
                      <p className="text-xs text-muted-foreground">
                        Last updated: {format(new Date(selectedEmail.updated_at), 'PPpp')}
                      </p>
                    </div>
                  )}
                </TabsContent>

                {/* TECHNICAL TAB */}
                <TabsContent value="technical" className="space-y-4 mt-0">
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-3">Tracking IDs</h4>
                    <div className="space-y-3">
                      {selectedEmail.stats_thread_id && (
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-muted-foreground">Thread ID</span>
                          <code className="text-xs bg-muted px-3 py-2 rounded block break-all font-mono">
                            {selectedEmail.stats_thread_id}
                          </code>
                        </div>
                      )}
                      
                      {selectedEmail.message_id && (
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-muted-foreground">Message ID</span>
                          <code className="text-xs bg-muted px-3 py-2 rounded block break-all font-mono">
                            {selectedEmail.message_id}
                          </code>
                        </div>
                      )}
                      
                      {selectedEmail.stats_id && (
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-muted-foreground">Stats ID</span>
                          <code className="text-xs bg-muted px-3 py-2 rounded block break-all font-mono">
                            {selectedEmail.stats_id}
                          </code>
                        </div>
                      )}
                      
                      {selectedEmail.sl_email_lead_id && (
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-muted-foreground">Smartlead Lead ID</span>
                          <code className="text-xs bg-muted px-3 py-2 rounded block break-all font-mono">
                            {selectedEmail.sl_email_lead_id}
                          </code>
                        </div>
                      )}
                      
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">Record ID</span>
                        <code className="text-xs bg-muted px-3 py-2 rounded block break-all font-mono">
                          {selectedEmail.id}
                        </code>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Timestamps</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Created:</span>
                        <span className="font-medium">{format(new Date(selectedEmail.created_at), 'PPpp')}</span>
                      </div>
                      {selectedEmail.updated_at && selectedEmail.updated_at !== selectedEmail.created_at && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Updated:</span>
                          <span className="font-medium">{format(new Date(selectedEmail.updated_at), 'PPpp')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </div>

              {/* Action Buttons - Always Visible */}
              <div className="flex justify-between items-center pt-4 border-t mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsDetailModalOpen(false)}
                >
                  Close
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    const details = `From: ${selectedEmail.from_email}\nTo: ${selectedEmail.to_email}\nSubject: ${selectedEmail.subject}\nCampaign: ${selectedEmail.campaign_name}\nDate: ${format(new Date(selectedEmail.created_at), 'PPpp')}\n\n${selectedEmail.body_text || selectedEmail.sent_message_text || ''}`
                    navigator.clipboard.writeText(details)
                    toast({
                      title: 'Copied!',
                      description: 'Email details copied to clipboard',
                    })
                  }}
                >
                  Copy Details
                </Button>
              </div>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
